// Copyright ©️ 2026 eVolve MEP, LLC

/**
 * Step 1 of the measurement: sort the selected dbIds into **supports** — every place a chain might be
 * drawn — and **conduit candidates**, everything that might carry a measurable centerline. Roles come from
 * properties where they say ({@link getRackElementLookup}), and from geometry where they don't.
 *
 * Centerlines are not read here: only elements near a support are ever measured, so
 * {@link RackElementCandidates.readConduitPipes} defers and memoizes that work per element.
 */

import { findElementCenterline } from 'components/Autodesk/extensions/DimensioningExtension/RackDimensions/measurement/candidatePreprocessing/findElementCenterline';
import {
  getRackElementLookup,
  type RackElementLookup,
} from 'components/Autodesk/extensions/DimensioningExtension/RackDimensions/measurement/candidatePreprocessing/getRackElementLookup';
import { MIN_MEASURABLE_LENGTH_FT } from 'components/Autodesk/extensions/DimensioningExtension/RackDimensions/measurement/geometryHelpers';
import type { RackPipe } from 'components/Autodesk/extensions/DimensioningExtension/RackDimensions/types';
import {
  type AutodeskFragmentList,
  getDbIdWorldBounds,
  getFragmentList,
  getObjectTree,
} from 'components/Autodesk/extensions/viewerHelpers';
import type { AutodeskDbId } from 'components/Autodesk/types';
import { THREE } from 'components/Autodesk/viewerThree';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { isInsignificant } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/helpers';

/** A selection sorted into the parts the support-anchored search runs over. */
export type RackElementCandidates = {
  /** World bounds per element that could provide a chain's reference edges. */
  supportWorldBounds: Map<AutodeskDbId, THREE.Box3>;
  /** World bounds per element that could carry conduit. Cheap to test a support against. */
  conduitWorldBounds: Map<AutodeskDbId, THREE.Box3>;
  /** The straight conduit centerlines of one element, read on first use and cached thereafter. */
  readConduitPipes: (dbId: AutodeskDbId) => RackPipe[];
  /**
   * Every conduit read so far, for tracing which of them are joined into one run. Call it once the supports
   * have pulled what they need through {@link readConduitPipes}.
   */
  tracedConduitPipes: () => RackPipe[];
  /** The world bounds of the whole selection. Its center is what makes a cross-section interior or not. */
  selectionWorldBounds: THREE.Box3;
};

/** The radius of the conduit an element carries, or `null` when it carries no diameter property. */
const readConduitRadiusFt = (diameters: RackElementLookup['diameters'] | undefined): number | null => {
  const diameterFt = diameters?.outsideDiameterFt ?? diameters?.diameterFt;
  return isNil(diameterFt) ? null : diameterFt / 2;
};

/** Build a conduit from its centerline, or `null` when the centerline is too short to give a direction. */
const toRackPipe = (dbId: AutodeskDbId, centerline: { start: THREE.Vector3; end: THREE.Vector3 }): RackPipe | null => {
  const axis = centerline.end.clone().sub(centerline.start);
  if (isInsignificant(axis.length(), MIN_MEASURABLE_LENGTH_FT)) return null;

  return {
    dbId,
    start: centerline.start,
    end: centerline.end,
    center: centerline.start.clone().add(centerline.end).multiplyScalar(0.5),
    axis: axis.normalize(),
  };
};

/**
 * Every straight conduit one element holds. An element yields as many as its centerline does: a bend
 * contributes a leg per side of its arc, and a nested assembly one per conduit it carries.
 */
const readElementPipes = (
  instanceTree: Autodesk.Viewing.InstanceTree,
  fragList: AutodeskFragmentList,
  dbId: AutodeskDbId,
  element: RackElementLookup | undefined,
): RackPipe[] =>
  findElementCenterline(instanceTree, fragList, dbId, readConduitRadiusFt(element?.diameters))
    .map((straightLength) => toRackPipe(dbId, straightLength))
    .filter(isNotNil);

/** Read the selection's properties, then sort it into supports and conduit candidates. */
export const buildRackElementCandidates = async (
  model: Autodesk.Viewing.Model,
  dbIds: AutodeskDbId[],
): Promise<RackElementCandidates> => {
  const [instanceTree, lookup] = await Promise.all([getObjectTree(model), getRackElementLookup(model, dbIds)]);
  const fragList = getFragmentList(model);

  // Build a cache of dbIds to Rack Pipe lists.
  const pipesByElement = new Map<AutodeskDbId, RackPipe[]>();
  const readConduitPipes = (dbId: AutodeskDbId): RackPipe[] => {
    const cached = pipesByElement.get(dbId);
    if (isNotNil(cached)) return cached;

    const pipes = readElementPipes(instanceTree, fragList, dbId, lookup.get(dbId));
    pipesByElement.set(dbId, pipes);
    return pipes;
  };

  const supportWorldBounds = new Map<AutodeskDbId, THREE.Box3>();
  const conduitWorldBounds = new Map<AutodeskDbId, THREE.Box3>();
  const selectionWorldBounds = new THREE.Box3();

  for (const dbId of dbIds) {
    const worldBounds = getDbIdWorldBounds(instanceTree, dbId);

    if (worldBounds.isEmpty()) continue;
    selectionWorldBounds.union(worldBounds);

    const role = lookup.get(dbId)?.role ?? null;
    if (role === 'support') {
      supportWorldBounds.set(dbId, worldBounds);
    } else if (role === 'conduit') {
      conduitWorldBounds.set(dbId, worldBounds);
    } else if (readConduitPipes(dbId).length > 0) {
      // No category at all, but it has a centerline — the only remaining evidence that it is conduit.
      conduitWorldBounds.set(dbId, worldBounds);
    } else {
      supportWorldBounds.set(dbId, worldBounds);
    }
  }

  return {
    supportWorldBounds,
    conduitWorldBounds,
    readConduitPipes,
    tracedConduitPipes: () => [...pipesByElement.values()].flat(),
    selectionWorldBounds,
  };
};
